import { useEffect, useRef, useState } from 'react'
import { Star, Tag, Users } from 'lucide-react'
import type { HouseholdMember, Todo, TodoTitle, UserId } from '@hewey/shared'
import { Checkbox } from '@components/Checkbox.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import { useMode } from '@mode/ModeContext.tsx'
import { colorFor } from '@utils/colors.ts'
import { AssigneeChips } from './AssigneeChips.tsx'
import { AssigneePicker } from './AssigneePicker.tsx'
import styles from './TodoItem.module.css'

type Props = {
	todo: Todo
	members: HouseholdMember[]
	memberMap: Map<UserId, HouseholdMember>
	onToggle: (next: boolean) => void
	onRename: (title: TodoTitle) => void
	onTagClick: () => void
	onPriorityToggle: () => void
	onAssign: (assigneeIds: UserId[]) => void
}

export const TodoItem = ({
	todo,
	members,
	memberMap,
	onToggle,
	onRename,
	onTagClick,
	onPriorityToggle,
	onAssign,
}: Props) => {
	const { mode } = useMode()
	const online = useOnline()
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(todo.title)
	const [assigneeOpen, setAssigneeOpen] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus()
			inputRef.current?.select()
		}
	}, [editing])

	const startEdit = () => {
		setDraft(todo.title)
		setEditing(true)
	}

	const commit = () => {
		const trimmed = draft.trim()
		if (trimmed.length === 0 || trimmed === todo.title) {
			setEditing(false)
			setDraft(todo.title)
			return
		}
		onRename(trimmed.slice(0, 200) as TodoTitle)
		setEditing(false)
	}

	const cancel = () => {
		setEditing(false)
		setDraft(todo.title)
	}

	const toggleAssignee = (id: UserId) => {
		const next = todo.assigneeIds.includes(id)
			? todo.assigneeIds.filter((x) => x !== id)
			: [...todo.assigneeIds, id]
		onAssign(next)
	}

	const tagColor = todo.category
		? colorFor(todo.category)
		: 'var(--ink-muted)'

	// Assignees only apply to family todos (personal todos are private).
	const showAssignees = mode === 'family'
	const hasAssignees = todo.assigneeIds.length > 0

	return (
		<li
			className={`${styles.item} ${todo.completed ? styles.done : ''}`.trim()}
		>
			<Checkbox
				checked={todo.completed}
				disabled={!online}
				onChange={onToggle}
				ariaLabel={todo.completed ? 'Mark as not done' : 'Mark as done'}
			/>

			{editing ? (
				<input
					ref={inputRef}
					className={styles.editor}
					value={draft}
					maxLength={200}
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault()
							commit()
						} else if (e.key === 'Escape') {
							e.preventDefault()
							cancel()
						}
					}}
				/>
			) : (
				<button
					type="button"
					className={styles.title}
					onClick={startEdit}
					disabled={!online}
				>
					{todo.title}
				</button>
			)}

			<div className={styles.controls}>
				{showAssignees && (
					<button
						type="button"
						className={styles.assign}
						onClick={() => setAssigneeOpen(true)}
						disabled={!online}
						aria-label={hasAssignees ? 'Edit assignees' : 'Assign'}
					>
						{hasAssignees ? (
							<AssigneeChips
								members={memberMap}
								ids={todo.assigneeIds}
							/>
						) : (
							<Users
								size={18}
								aria-hidden="true"
								style={{ color: 'var(--ink-muted)' }}
							/>
						)}
					</button>
				)}

				<button
					type="button"
					className={styles.star}
					style={{
						color: todo.priority
							? 'var(--accent-sunshine)'
							: 'var(--ink-muted)',
					}}
					onClick={onPriorityToggle}
					disabled={!online}
					aria-label={
						todo.priority ? 'Remove priority' : 'Mark as priority'
					}
					aria-pressed={todo.priority}
				>
					<Star
						size={18}
						aria-hidden="true"
						fill={todo.priority ? 'currentColor' : 'none'}
					/>
				</button>

				<button
					type="button"
					className={styles.tag}
					style={{ color: tagColor }}
					onClick={onTagClick}
					disabled={!online}
					aria-label={
						todo.category
							? `Category: ${todo.category}`
							: 'Set category'
					}
				>
					<Tag size={18} aria-hidden="true" />
				</button>
			</div>

			{showAssignees && (
				<AssigneePicker
					open={assigneeOpen}
					members={members}
					selected={todo.assigneeIds}
					onToggle={toggleAssignee}
					onClose={() => setAssigneeOpen(false)}
				/>
			)}
		</li>
	)
}
